import {SerializedError} from '@reduxjs/toolkit'

import {ErrorResponse} from '../../booking/types/ErrorResponse'
import {FetchBaseQueryError} from '../../utils/network/fetchBaseQuery'
import {ServerErrorContext} from '../types/Events'

/**
 * Error payload normalized by a redux error action, which carries the response
 * body under `details` rather than `data`.
 */
export interface NormalizedErrorPayload {
  status?: number
  headers?: Record<string, unknown>
  message?: string
  details?: ErrorResponse
}

type ServerError =
  | FetchBaseQueryError
  | SerializedError
  | NormalizedErrorPayload

// The union above spells out which callers are supported; reading the fields
// off one superset avoids an `in` check per field.
interface ServerErrorFields {
  status?: number
  headers?: Record<string, unknown>
  message?: string
  data?: ErrorResponse | string
  details?: ErrorResponse
}

interface FailedRequest {
  url?: string
  method?: string
}

/**
 * Maps a server error onto the analytics server error context.
 * @param error - The error, either rejected by RTK Query or normalized by a redux error action
 * @param request - Optional, Url and method of the failed request
 * @returns The server error context, or null when there is no error
 */
export const getServerErrorContext = (
  error?: ServerError | null,
  request: FailedRequest = {}
): ServerErrorContext | null => {
  if (!error) return null

  const {status, headers, message, data, details} = error as ServerErrorFields
  // fetchBaseQuery puts raw network-error text straight into `data`
  const responseText = typeof data === 'string' ? data : undefined
  const response = typeof data === 'string' ? undefined : data
  const {url, method} = request

  return {
    statusCode: status || 500,
    headers: headers || {},
    errorResponse: response || details || {},
    errorMessage: message || response?.message || responseText || '',
    ...(url ? {url} : {}),
    ...(method ? {method} : {})
  }
}
